import type { User } from "@/types";
import { orgScopedPath } from "@/lib/orgDoor";

/**
 * The role-appropriate landing page for a user. Every authenticated role
 * resolves to a shell-wrapped home (sidebar + nav) — never the bare public
 * "/" landing. Order matters: admin wins over any secondary role.
 *
 * Shared by the legal-onboarding redirect (post terms/NDA) and the "/" guard,
 * so a user can never get stranded on the public home once logged in.
 */
export function homePathForUser(u: User): string {
  const roles = u.roles ?? [];
  // Admins of a non-default organization work under /<slug>/admin/...
  // Either admin tier lands in the admin console; org_admins under their
  // organization prefix.
  if (roles.includes("admin") || roles.includes("org_admin"))
    return orgScopedPath("/admin/home", u.org_slug);
  if (roles.includes("bl_advisor")) return orgScopedPath("/bl-advisor/home", u.org_slug);
  if (roles.includes("seller")) return orgScopedPath("/seller/home", u.org_slug);
  if (roles.includes("buyer")) {
    // The dashboard, not the marketplace: it is the one page that shows a buyer
    // where every deal of theirs stands, and links out to each module from
    // there. Free (Basic) buyers land here too — GET /dashboard/buyer is not
    // behind the subscription gate, and each card degrades to its own empty or
    // upgrade state rather than 402-ing the landing page.
    return orgScopedPath("/buyer/dashboard", u.org_slug);
  }
  // No recognized role yet (e.g. roles not hydrated): fall back to the seller
  // listings view, which is reachable by every non-buyer role.
  return orgScopedPath("/listings/mine", u.org_slug);
}

/**
 * The Documents tab of the current user's own settings — where their signed NDA
 * and accepted Terms live. Same role order as {@link homePathForUser}: the tab
 * only exists on the buyer / advisor / admin settings pages, so sellers (still
 * on the legacy single-page profile) get that page instead.
 */
export function documentsPathForUser(u: User): string {
  const roles = u.roles ?? [];
  if (roles.includes("admin") || roles.includes("org_admin"))
    return orgScopedPath("/admin/settings/documents", u.org_slug);
  if (roles.includes("bl_advisor")) return orgScopedPath("/bl-advisor/settings/documents", u.org_slug);
  if (roles.includes("buyer")) return orgScopedPath("/buyer/settings/documents", u.org_slug);
  return orgScopedPath("/seller/profile", u.org_slug);
}

/**
 * Sanitise a `?next=` destination before navigating to it.
 *
 * Only same-site absolute paths survive: anything else — an absolute URL, or a
 * protocol-relative "//evil.example" that the browser would treat as one — is
 * dropped so a crafted sign-in link can never bounce a user off-site after they
 * hand over their password.
 */
export function safeRedirectPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // "//host" and "/\host" are both read as protocol-relative URLs by browsers.
  if (!raw.startsWith("/") || raw[1] === "/" || raw[1] === "\\") return null;
  return raw;
}
